import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * "No clinically relevant findings" toggle, keyed by patient guid.
 *
 * Phase 1 spec — Phenotypes user story #11. The flag tells the section
 * to hide its rows and disable phenotype entry, expressing the clinical
 * statement "I reviewed phenotypes and found none of interest" rather
 * than "I haven't recorded any yet".
 *
 * The BE doesn't yet expose a flag on the person record (PTV2-1051 #4
 * notes this); until it does, we persist client-side to sessionStorage
 * so the choice survives navigation within a tab. Reload-across-tabs is
 * not preserved by design — pilot deployments shouldn't rely on this
 * lasting longer than a session. Swap for the BE flag once available.
 */
interface NoFindingsState {
  readonly byGuid: Readonly<Record<string, boolean>>;
  readonly set: (guid: string, next: boolean) => void;
}

export const useNoFindingsStore = create<NoFindingsState>()(
  persist(
    (set) => ({
      byGuid: {},
      set: (guid, next) => {
        set((state) => {
          if ((state.byGuid[guid] ?? false) === next) return state;
          const updated = { ...state.byGuid };
          if (next) {
            updated[guid] = true;
          } else {
            delete updated[guid];
          }
          return { byGuid: updated };
        });
      },
    }),
    {
      name: 'ptv2.phenotypes.noFindings',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ byGuid: state.byGuid }),
    },
  ),
);

/** Selector helper — `true` when the flag is set for `guid`. */
export function noFindingsFor(
  state: NoFindingsState,
  guid: string,
): boolean {
  return state.byGuid[guid] ?? false;
}
